// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import {
  type MachineElement,
  useEvolvePost,
  type Project,
  type WorkOrderPaginated,
  type Facility,
  type WorkOrderItem,
  useEvolvePaginatedGet,
} from '@evolvemep/api-ts';
import { Box, Button, Stack, TextInput } from '@mantine/core';
import { useForm, type UseFormReturnType } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import type { WithNullable } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { WorkOrderSearchableSelectForFabrication } from 'modules/Shop/EvolveFabrication/WorkOrderSearchableSelectForFabrication';

type Props = {
  onElementsAdded: () => void;
  selectedElements: MachineElement[];
  selectedFacilities: Facility[];
  selectedProjects: Project[];
  /** When provided, the work order is pre-selected and locked */
  workOrder?: WorkOrderPaginated;
};

/** The item the elements get added to: one already on the work order, or one created on submit. */
type WorkOrderItemChoice = { existingItem: WorkOrderItem } | { newItemName: string };

type ValidatedFormBody = {
  workOrder: WorkOrderPaginated;
  workOrderItem: WorkOrderItemChoice;
};

type FormBody = WithNullable<ValidatedFormBody, 'workOrder' | 'workOrderItem'>;

/**
 * Renders a select input for choosing an existing work order item or creating a new one.
 * Should only render when a work order is selected, since a work order is required by the shop/workOrder/item query parameters
 **/
const SelectWorkOrderItem = ({
  workOrder,
  form,
}: {
  workOrder: WorkOrderPaginated;
  form: UseFormReturnType<FormBody>;
}) => {
  const defaultOpts = useMemo(
    () => ({ defaultConfig: { params: { workOrderId: workOrder.workOrderId } } }),
    [workOrder],
  );
  const { setDefaultOpts: setDefaultOptsWorkOrderItems, ...workOrderItemPaginatedGet } = useEvolvePaginatedGet(
    'shop/workOrder/item',
    defaultOpts,
  );
  useEffect(() => {
    setDefaultOptsWorkOrderItems(defaultOpts);
  }, [defaultOpts, setDefaultOptsWorkOrderItems]);

  const { workOrderItem } = form.values;

  return (
    <SearchableSelect<'shop/workOrder/item', WorkOrderItem, 'workOrderItemId'>
      label="Item"
      autoDropdownWidth
      required
      creatable
      createLabel="Create new work order item"
      getItemLabel={(item) => item.name}
      idKey="workOrderItemId"
      searchKey="name"
      paginatedGet={workOrderItemPaginatedGet}
      {...form.getInputProps('workOrderItem')}
      value={isNotNil(workOrderItem) && 'existingItem' in workOrderItem ? workOrderItem.existingItem : null}
      createdValue={isNotNil(workOrderItem) && 'newItemName' in workOrderItem ? workOrderItem.newItemName : null}
      onChange={(item) => form.setFieldValue('workOrderItem', isNil(item) ? null : { existingItem: item })}
      onCreate={(newItemName) => form.setFieldValue('workOrderItem', { newItemName })}
    />
  );
};

const AddElementsFields = ({
  onElementsAdded,
  selectedElements,
  selectedFacilities,
  selectedProjects,
  workOrder,
}: Props) => {
  const { apiCall: mapElementsToWorkOrderApiCall } = useEvolvePost('shop/workOrder/:workOrderId/mapElements');
  const { apiCall: mapElementsToWorkOrderItemApiCall } = useEvolvePost(
    'shop/workOrder/item/:workOrderItemId/mapElements',
  );
  const form = useForm<FormBody>({
    initialValues: {
      workOrder: workOrder ?? null,
      workOrderItem: null,
    },
    validate: {
      workOrder: (v) => isNil(v),
      workOrderItem: (v) => isNil(v),
    },
  });

  const onSubmit = async (rawValues: typeof form.values) => {
    const { workOrder: selectedWorkOrder, workOrderItem } = rawValues as ValidatedFormBody;
    const elementIds = selectedElements.map((e) => e.elementId);

    if ('newItemName' in workOrderItem) {
      await mapElementsToWorkOrderApiCall(
        { elementIds, workOrderItemName: workOrderItem.newItemName },
        { url: `shop/workOrder/${selectedWorkOrder.workOrderId}/mapElements` },
      );
    } else {
      await mapElementsToWorkOrderItemApiCall(
        { elementIds },
        { url: `shop/workOrder/item/${workOrderItem.existingItem.workOrderItemId}/mapElements` },
      );
    }

    notifications.show({
      title: 'Successfully added',
      message: `Added ${selectedElements.length} part${selectedElements.length === 1 ? '' : 's'} to ${selectedWorkOrder.name}.`,
      color: 'green',
    });

    onElementsAdded();
  };

  useFormPopover(form, onSubmit);

  const currentWorkOrder = form.values.workOrder;
  return (
    <Stack gap="xs">
      {isNil(workOrder) && (
        <WorkOrderSearchableSelectForFabrication
          selectedProjects={selectedProjects}
          selectedFacilities={selectedFacilities}
          autoFocus
          data-autofocus
          {...form.getInputProps('workOrder')}
          onChange={(v) => form.setValues({ workOrder: v, workOrderItem: null })}
        />
      )}
      {isNil(currentWorkOrder) ? (
        <TextInput label="Item Name" required disabled />
      ) : (
        <SelectWorkOrderItem workOrder={currentWorkOrder} form={form} />
      )}
    </Stack>
  );
};

export const AddElementsToWorkOrderPopover = (props: Props) => {
  const { selectedElements } = props;
  const selectedElementAlreadyOnWorkOrder = selectedElements.some((element) => isNotNil(element.workOrderId));
  const [opened, setOpened] = useState(false);

  return (
    <FormPopover
      opened={opened}
      onClose={() => setOpened(false)}
      confirmationText="Add"
      confirmationCount={selectedElements.length}
      startDirty
      target={
        <Box>
          <AgGridStyleTooltip
            label="At least one item is already on a work order"
            openDelay={0}
            disabled={!selectedElementAlreadyOnWorkOrder}
          >
            <Button
              onClick={() => setOpened(true)}
              disabled={selectedElementAlreadyOnWorkOrder || selectedElements.length === 0}
              leftSection={<EvolveIcon icon="Add" size="sm" />}
            >
              Add to work order
            </Button>
          </AgGridStyleTooltip>
        </Box>
      }
    >
      <AddElementsFields {...props} />
    </FormPopover>
  );
};
